import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { staffApi } from '@/services/api';
import { HiOutlineArrowLeft } from 'react-icons/hi';

const staffSchema = z.object({
  staffNumber: z.string().min(1, '職員番号を入力してください'),
  lastName: z.string().min(1, '姓を入力してください'),
  firstName: z.string().min(1, '名を入力してください'),
  lastNameKana: z.string().optional(),
  firstNameKana: z.string().optional(),
  birthDate: z.string().optional(),
  gender: z.enum(['male', 'female', 'other']).optional(),
  postalCode: z.string().optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  mobilePhone: z.string().optional(),
  email: z.string().email().optional().or(z.literal('')),
  employmentType: z.enum(['full_time', 'part_time', 'contract', 'temporary']).optional(),
  position: z.string().optional(),
  jobCategory: z.string().optional(),
  hireDate: z.string().optional(),
  workHoursPerWeek: z.number().optional(),
  hourlyWage: z.number().optional(),
  monthlySalary: z.number().optional(),
  notes: z.string().optional(),
});

type StaffForm = z.infer<typeof staffSchema>;

export default function StaffCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<StaffForm>({
    resolver: zodResolver(staffSchema),
  });

  const mutation = useMutation({
    mutationFn: (data: StaffForm) => staffApi.create(data),
    onSuccess: () => {
      toast.success('職員を登録しました');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      navigate('/staff');
    },
    onError: (error: Error) => {
      toast.error(error.message || '登録に失敗しました');
    },
  });

  const onSubmit = (data: StaffForm) => {
    mutation.mutate(data);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/staff')} className="btn-ghost">
          <HiOutlineArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">職員登録</h1>
          <p className="text-gray-600">新しい職員を登録します</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* 基本情報 */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">基本情報</h2>
          </div>
          <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">職員番号 *</label>
              <input {...register('staffNumber')} className={errors.staffNumber ? 'input-error' : 'input'} />
              {errors.staffNumber && <p className="text-sm text-red-600 mt-1">{errors.staffNumber.message}</p>}
            </div>
            <div className="md:col-span-2 grid grid-cols-2 gap-4">
              <div>
                <label className="label">姓 *</label>
                <input {...register('lastName')} className={errors.lastName ? 'input-error' : 'input'} />
                {errors.lastName && <p className="text-sm text-red-600 mt-1">{errors.lastName.message}</p>}
              </div>
              <div>
                <label className="label">名 *</label>
                <input {...register('firstName')} className={errors.firstName ? 'input-error' : 'input'} />
                {errors.firstName && <p className="text-sm text-red-600 mt-1">{errors.firstName.message}</p>}
              </div>
            </div>
            <div className="md:col-span-2 grid grid-cols-2 gap-4">
              <div>
                <label className="label">セイ</label>
                <input {...register('lastNameKana')} className="input" />
              </div>
              <div>
                <label className="label">メイ</label>
                <input {...register('firstNameKana')} className="input" />
              </div>
            </div>
            <div>
              <label className="label">生年月日</label>
              <input type="date" {...register('birthDate')} className="input" />
            </div>
            <div>
              <label className="label">性別</label>
              <select {...register('gender')} className="input">
                <option value="">選択してください</option>
                <option value="male">男性</option>
                <option value="female">女性</option>
                <option value="other">その他</option>
              </select>
            </div>
            <div>
              <label className="label">電話番号</label>
              <input {...register('phone')} className="input" />
            </div>
            <div>
              <label className="label">携帯電話</label>
              <input {...register('mobilePhone')} className="input" />
            </div>
            <div className="md:col-span-2">
              <label className="label">メールアドレス</label>
              <input type="email" {...register('email')} className="input" />
            </div>
          </div>
        </div>

        {/* 雇用情報 */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">雇用情報</h2>
          </div>
          <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">雇用形態</label>
              <select {...register('employmentType')} className="input">
                <option value="">選択してください</option>
                <option value="full_time">正社員</option>
                <option value="part_time">パート</option>
                <option value="contract">契約社員</option>
                <option value="temporary">派遣</option>
              </select>
            </div>
            <div>
              <label className="label">役職</label>
              <input {...register('position')} className="input" />
            </div>
            <div>
              <label className="label">職種</label>
              <input {...register('jobCategory')} className="input" />
            </div>
            <div>
              <label className="label">入社日</label>
              <input type="date" {...register('hireDate')} className="input" />
            </div>
            <div>
              <label className="label">週労働時間</label>
              <input type="number" step="0.5" {...register('workHoursPerWeek', { valueAsNumber: true })} className="input" />
            </div>
            <div>
              <label className="label">時給</label>
              <input type="number" {...register('hourlyWage', { valueAsNumber: true })} className="input" />
            </div>
            <div>
              <label className="label">月給</label>
              <input type="number" {...register('monthlySalary', { valueAsNumber: true })} className="input" />
            </div>
          </div>
        </div>

        {/* 備考 */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">備考</h2>
          </div>
          <div className="card-body">
            <textarea {...register('notes')} rows={4} className="input" />
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button type="button" onClick={() => navigate('/staff')} className="btn-secondary">
            キャンセル
          </button>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? '登録中...' : '登録する'}
          </button>
        </div>
      </form>
    </div>
  );
}


